import { useEffect, useState } from 'react';
import trophyIcon from '@/assets/trophy-16bit.webp';
import { KaijuId, Algorithm, KAIJU } from '@/lib/gameData';
import { getKaijuName, getKaijuDifficulty, getKaijuImage } from '@/lib/gameDataOverrides';
import { primeGameAudio, unlockAudio } from '@/lib/audioUnlock';
import { getIntroMusic, getOutroMusic } from '@/lib/gameDataOverrides';

interface SetupScreenProps {
  onStart: (playerName: string, cityName: string, kaijuId: KaijuId, algorithm: Algorithm) => void;
  onViewHighScores?: () => void;
}

const kaijuOptions: KaijuId[] = ['rodan', 'mothra', 'mechagodzilla', 'ghidorah'];

const SetupScreen = ({ onStart, onViewHighScores }: SetupScreenProps) => {
  const [kaiju, setKaiju] = useState<KaijuId | null>(null);
  const [algorithm, setAlgorithm] = useState<Algorithm>('START');
  const [, setHydrationTick] = useState(0);

  useEffect(() => {
    const onHydrated = () => setHydrationTick(t => t + 1);
    window.addEventListener('settings-hydrated', onHydrated);
    return () => window.removeEventListener('settings-hydrated', onHydrated);
  }, []);

  const canStart = !!kaiju;

  return (
    <div className="h-dvh flex flex-col">
      <div className="flex-1 overflow-y-auto relative">
        <img
          src="/images/em-ronin-logo.webp"
          alt="EM Ronin"
          className="absolute top-4 left-4 z-10 w-12 h-12 sm:w-14 sm:h-14 object-contain"
        />
        {onViewHighScores && (
          <button
            onClick={onViewHighScores}
            className="absolute top-4 right-4 z-10 p-2 rounded-lg border border-border bg-background/80 backdrop-blur-sm hover:bg-muted/50 transition-colors"
          >
            <img src={trophyIcon} alt="High Scores" className="sm:hidden w-7 h-7 object-contain" />
            <span className="hidden sm:block font-mono-game text-sm text-muted-foreground hover:text-foreground transition-colors">HIGH SCORES</span>
          </button>
        )}
        <div className="flex items-start justify-center p-4 min-h-full">
          <div className="w-full max-w-2xl space-y-8 animate-fade-in py-8">
            <div className="text-center space-y-2">
              <h1 className="text-5xl md:text-6xl font-bold font-display tracking-tight text-glow-cyan text-primary">
                MCI TRIAGE
              </h1>
              <p className="text-lg font-mono-game text-muted-foreground">
                KAIJU EMERGENCY RESPONSE TRAINING
              </p>
            </div>

            {/* Kaiju Selection */}
            <div className="space-y-3">
              <label className="text-base font-mono-game text-muted-foreground uppercase tracking-widest">
                SELECT KAIJU
              </label>
              <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
                {kaijuOptions.map(id => {
                  const selected = kaiju === id;
                  const diff = getKaijuDifficulty(id);
                  const imageUrl = getKaijuImage(id);
                  const { name, description } = getKaijuName(id);

                  return (
                    <button
                      key={id}
                      onClick={() => setKaiju(id)}
                      className={`rounded-lg border-2 text-left transition-all duration-200 relative overflow-hidden ${
                        selected
                          ? 'border-primary shadow-[0_0_15px_hsl(var(--primary)/0.3)]'
                          : 'border-border bg-card hover:border-muted-foreground/40'
                      }`}
                    >
                      {/* Difficulty badge - always top right */}
                      {diff && (
                        <span className="absolute top-2 right-2 z-10 text-xs font-mono-game font-bold uppercase tracking-wider px-2 py-0.5 rounded bg-black/60 text-white border border-white/20 backdrop-blur-sm">
                          {diff}
                        </span>
                      )}

                      {imageUrl ? (
                        selected ? (
                          /* Selected with image: show details with faded background */
                          <div className="relative p-4 aspect-[4/3] flex flex-col justify-center">
                            <div
                              className="absolute inset-0 opacity-[0.12]"
                              style={{
                                backgroundImage: `url(${imageUrl})`,
                                backgroundSize: 'cover',
                                backgroundPosition: 'center',
                              }}
                            />
                            <div className="relative z-[1]">
                              <div className="text-xl font-bold text-primary mb-1">{name}</div>
                              <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
                            </div>
                          </div>
                        ) : (
                          /* Unselected with image: show image as card face */
                          <div className="relative aspect-[4/3]">
                            <img src={imageUrl} alt={name} width={650} height={488} className="w-full h-full object-cover" loading="eager" decoding="async" fetchPriority="high" />
                            
                            <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/80 via-black/40 to-transparent p-3 pt-8">
                              <span className="text-white font-bold text-lg drop-shadow-lg">{name}</span>
                            </div>
                          </div>
                        )
                      ) : (
                        /* No image fallback: text-only layout */
                        <div className="p-4">
                          <div className="text-2xl mb-1">{name}</div>
                          <p className="text-sm text-muted-foreground leading-relaxed">{description}</p>
                        </div>
                      )}
                    </button>
                  );
                })}
              </div>
            </div>

            {/* Algorithm */}
            <div className="space-y-2">
              <label className="text-base font-mono-game text-muted-foreground uppercase tracking-widest">
                SELECT TRIAGE ALGORITHM
              </label>
              <div className="flex gap-3">
                {(['START', 'SALT'] as Algorithm[]).map(a => (
                  <button
                    key={a}
                    onClick={() => { if (a === 'START') setAlgorithm(a); }}
                    disabled={a === 'SALT'}
                    className={`flex-1 py-3 rounded-lg border-2 font-mono-game text-lg font-bold transition-all relative ${
                      a === 'SALT'
                        ? 'border-border bg-card/50 text-muted-foreground/40 cursor-not-allowed'
                        : algorithm === a
                          ? 'border-primary bg-primary/10 text-primary'
                          : 'border-border bg-card text-muted-foreground hover:border-muted-foreground/40'
                    }`}
                  >
                    {a}
                    {a === 'START' && (
                      <span className="block text-xs font-normal text-muted-foreground/70 mt-0.5">Simple Triage and Rapid Treatment</span>
                    )}
                    {a === 'SALT' && (
                      <span className="block text-[10px] font-normal text-muted-foreground/50 mt-0.5">Coming Soon</span>
                    )}
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
        {/* Scroll fade hint */}
        <div className="sticky bottom-0 left-0 right-0 h-12 bg-gradient-to-t from-background to-transparent pointer-events-none" />
      </div>

      {/* Fixed Deploy Button */}
      <div className="shrink-0 border-t border-border bg-background/95 backdrop-blur-sm px-4 pt-4 pb-8 shadow-[0_-4px_20px_rgba(0,0,0,0.3)]">
        <div className="max-w-2xl mx-auto">
          <button
            disabled={!canStart}
            onClick={() => { if (canStart) { unlockAudio(); primeGameAudio(getIntroMusic(), getOutroMusic()); onStart('', 'Your City', kaiju!, algorithm); } }}
            className={`w-full py-4 rounded-lg font-display text-2xl font-bold uppercase tracking-wider transition-all ${
              canStart
                ? 'bg-primary text-primary-foreground hover:shadow-[0_0_30px_hsl(var(--primary)/0.5)] hover:scale-[1.02]'
                : 'bg-muted text-muted-foreground cursor-not-allowed'
            }`}
          >
            Deploy
          </button>
        </div>
      </div>
    </div>
  );
};

export default SetupScreen;
